import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  RefreshCw, Search, Calendar, Radio, Clock, CheckCircle,
  Eye, EyeOff, ExternalLink, Copy, ChevronUp, ChevronDown,
  Download, AlertCircle, Tv, Image, X, Check, Pencil
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthContext'
import { liveStatusLabel, liveStatusClass } from '../../../utils/liveStatus'
import CatalogDetail from '../../ondemand-library/content-catalog/CatalogDetail'
import './LiveSchedule.css'

const eventIcons = {
  upcoming: <Clock size={11} />,
  live: <Radio size={11} />,
  completed: <CheckCircle size={11} />,
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  })
}

function formatRelative(dateStr) {
  if (!dateStr) return ''
  const diff = new Date(dateStr).getTime() - Date.now()
  if (diff < 0) return 'past'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 48) return `in ${Math.floor(hours / 24)}d`
  if (hours > 0) return `in ${hours}h ${minutes}m`
  return `in ${minutes}m`
}

export default function LiveSchedule() {
  const { api } = useAuth()
  const navigate = useNavigate()

  const [videos, setVideos] = useState([])
  const [channels, setChannels] = useState([])
  const [tbtvChannels, setTbtvChannels] = useState([])
  const [cfLiveInputs, setCfLiveInputs] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [channelFilter, setChannelFilter] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [csvPreview, setCsvPreview] = useState(null)
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('asc')
  const [thumbEditId, setThumbEditId] = useState(null)   // youtube_id of row being edited
  const [thumbDraft, setThumbDraft] = useState('')        // draft URL while editing
  // In-place live-event editor (reuses the content-catalog detail editor). Live Schedule is an
  // alternate live-events view of the catalog; opening a row edits the SAME record via the SAME
  // endpoints, so both stay in sync.
  const [editItem, setEditItem] = useState(null)          // full /vod item being edited (null = closed)
  const [editLoading, setEditLoading] = useState(false)
  const [allTags, setAllTags] = useState([])

  // Same-input proximity warning (non-blocking). Two live events that share one CF input whose STARTS fall within
  // max_early_live_minutes (15) force a "needs operator" hand-off: the later event's pre-live (starting-soon)
  // window opens before the earlier can go live, so the coordinator sees two in-window events on one input and
  // refuses to auto-start (M2). We just FLAG the rows so the operator can space them out or expect the manual
  // Go-Live. (15 = the max_early_live_minutes default — keep in sync if that Live Setting changes.)
  const PRELIVE_MIN = 15
  const sharedInputRiskIds = useMemo(() => {
    const risky = new Set()
    const cand = (videos || []).filter(v => v.tbtv_id && v.cf_live_input_id && v.scheduled_start && v.live_v2_status !== 'ended')
    for (let i = 0; i < cand.length; i++) {
      for (let j = i + 1; j < cand.length; j++) {
        if (cand[i].cf_live_input_id !== cand[j].cf_live_input_id) continue
        const a = Date.parse(cand[i].scheduled_start), b = Date.parse(cand[j].scheduled_start)
        if (Number.isNaN(a) || Number.isNaN(b)) continue
        if (Math.abs(a - b) < PRELIVE_MIN * 60000) { risky.add(cand[i].tbtv_id); risky.add(cand[j].tbtv_id) }
      }
    }
    return risky
  }, [videos])

  const loadVideos = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch YouTube archive events
      const params = new URLSearchParams()
      if (!showAll) params.set('schedule', 'true')
      params.set('event_type', 'upcoming,live')
      if (channelFilter) params.set('channel', channelFilter)
      if (searchTerm) params.set('search', searchTerm)
      const ytData = await api(`/youtube/videos?${params}`)
      const ytVideos = ytData.videos || []

      // Fetch content library live events
      const clParams = new URLSearchParams()
      clParams.set('event_mode', 'live')
      if (searchTerm) clParams.set('search', searchTerm)
      const clData = await api(`/vod?${clParams}`)
      const clItems = (clData.items || [])
        // Exclude items that came from YouTube (already in ytVideos)
        .filter(item => !item.youtube_id || !ytVideos.some(v => v.youtube_id === item.youtube_id))
        // Map to the same shape as youtube_videos
        .map(item => ({
          youtube_id: item.youtube_id || null,
          tbtv_id: item.tbtv_id,
          title: item.title || 'Untitled',
          description: item.short_description || '',
          event_type: item.event_type || 'upcoming',
          scheduled_start: item.scheduled_start || null,
          actual_start: item.actual_start || null,
          live_v2_status: item.live_v2_status || null,
          auto_start: item.auto_start == null ? 1 : item.auto_start,
          thumbnail_url: item.thumbnail_url || '',
          thumbnail_override_url: null,
          youtube_channel_id: null,
          viloud_channel_name: item.viloud_channel_name || '',
          viloud_channel_id: item.viloud_channel_id || '',
          cf_live_input_id: item.cf_live_input_id || '',
          duration_minutes: item.duration_minutes || 120,
          include_in_schedule: item.include_in_schedule,
          stream_notes: '',
          _source: 'content-library'  // Track source for toggle routing
        }))

      setVideos([...ytVideos, ...clItems])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api, showAll, channelFilter, searchTerm])

  const loadChannels = useCallback(async () => {
    try {
      const data = await api('/youtube/channels')
      setChannels(data.channels || [])
    } catch {}
  }, [api])

  const loadPickers = useCallback(async () => {
    try { setTbtvChannels((await api('/channels')).channels || []) } catch {}
    try { setCfLiveInputs((await api('/live-inputs')).liveInputs || []) } catch {}
    try { setAllTags((await api('/tags')).tags || []) } catch {}
  }, [api])

  useEffect(() => { loadChannels(); loadPickers() }, [loadChannels, loadPickers])
  useEffect(() => { loadVideos() }, [loadVideos])

  // Open the shared catalog editor for a content-library live event (fetch the full record first).
  async function openEditor(video) {
    if (!video?.tbtv_id) { setStatus('Error: this event is not editable from here'); return }
    setEditLoading(true)
    try {
      const full = await api(`/vod/${video.tbtv_id}`)
      setEditItem(full)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setEditLoading(false)
  }

  // After any save in the editor, refresh the schedule and re-pull the record. If it left live mode
  // (e.g. Move to On-Demand), close the editor — it's no longer a Live Schedule item.
  async function handleEditorUpdate() {
    loadVideos()
    if (!editItem?.tbtv_id) return
    try {
      const fresh = await api(`/vod/${editItem.tbtv_id}`)
      if (fresh?.event_mode !== 'live') { setEditItem(null); return }
      setEditItem(fresh)
    } catch { /* keep the current editor state */ }
  }

  async function handleEditorDelete(tbtvId) {
    if (!confirm('Delete this item? This cannot be undone.')) return
    try {
      await api(`/vod/${tbtvId}`, { method: 'DELETE' })
      setStatus('✅ Deleted')
      setEditItem(null)
      loadVideos()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleEditorDuplicate() {
    // A duplicate is a new draft in the catalog — close here and refresh the schedule.
    setEditItem(null)
    loadVideos()
  }

  async function toggleSchedule(video) {
    const newVal = !video.include_in_schedule
    try {
      if (video._source === 'content-library') {
        // Content library event — update vod_catalog only
        await api(`/vod/${video.tbtv_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ include_in_schedule: newVal ? 1 : 0 })
        })
        setVideos(prev => prev.map(v =>
          v.tbtv_id === video.tbtv_id
            ? { ...v, include_in_schedule: newVal }
            : v
        ))
      } else {
        // YouTube archive event — update youtube_videos
        await api(`/youtube/videos/${video.youtube_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ include_in_schedule: newVal })
        })
        // Also sync to vod_catalog if imported to content library
        if (video.tbtv_id) {
          await api(`/vod/${video.tbtv_id}`, {
            method: 'PATCH',
            body: JSON.stringify({ include_in_schedule: newVal ? 1 : 0 })
          }).catch(() => {})
        }
        setVideos(prev => prev.map(v =>
          v.youtube_id === video.youtube_id
            ? { ...v, include_in_schedule: newVal }
            : v
        ))
      }
      setStatus(`✅ ${video.title} ${newVal ? 'added to' : 'removed from'} schedule`)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function updateField(video, field, value) {
    try {
      if (video._source === 'content-library') {
        // Content library event — update vod_catalog
        await api(`/vod/${video.tbtv_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ [field]: value })
        })
        setVideos(prev => prev.map(v =>
          v.tbtv_id === video.tbtv_id ? { ...v, [field]: value } : v
        ))
      } else {
        // YouTube archive event
        await api(`/youtube/videos/${video.youtube_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ [field]: value })
        })
        setVideos(prev => prev.map(v =>
          v.youtube_id === video.youtube_id ? { ...v, [field]: value } : v
        ))
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Operator End from the schedule (parity with the content-catalog live editor). Stream-aware:
  // the coordinator returns needsConfirm if the CF stream is still hot → second confirmation.
  async function endEvent(video, force = false) {
    if (!video.tbtv_id || !video.cf_live_input_id) { setStatus('Error: not a CF live event'); return }
    if (!force && !window.confirm(`End "${video.title}"?\n\nThe next scheduled event on this input/channel will then follow normal live logic.`)) return
    try {
      const r = await api(`/live/${video.tbtv_id}/end`, { method: 'POST', body: JSON.stringify({ reason: 'operator end (live schedule)', force }) })
      if (r?.coordinator?.needsConfirm) {
        if (window.confirm('⚠ The live stream is STILL ACTIVE.\n\nEnd this event anyway while it is still streaming?')) return endEvent(video, true)
        return
      }
      setStatus(`✅ Ended "${video.title}". Next event will come up per normal live logic.`)
      loadVideos()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Manual Go Live from the schedule (parity with the catalog): for Auto-Start OFF events awaiting go-live.
  async function goLiveNow(video) {
    if (!video.tbtv_id || !video.cf_live_input_id) { setStatus('Error: not a CF live event'); return }
    if (!window.confirm(`Go live now with "${video.title}"?\n\nThis event will switch to LIVE immediately (once the coordinator sees the stream).`)) return
    try {
      await api(`/live/${video.tbtv_id}/golive`, { method: 'POST', body: JSON.stringify({ reason: 'operator go-live (live schedule)' }) })
      setStatus(`✅ Going live with "${video.title}".`)
      loadVideos()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function saveThumbOverride(video, explicitValue) {
    const url = explicitValue !== undefined ? explicitValue : (thumbDraft.trim() || null)
    try {
      if (video._source === 'content-library') {
        await api(`/vod/${video.tbtv_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ thumbnail_url: url || '' })
        })
        setVideos(prev => prev.map(v =>
          v.tbtv_id === video.tbtv_id ? { ...v, thumbnail_url: url } : v
        ))
      } else {
        await api(`/youtube/videos/${video.youtube_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ thumbnail_override_url: url })
        })
        setVideos(prev => prev.map(v =>
          v.youtube_id === video.youtube_id ? { ...v, thumbnail_override_url: url } : v
        ))
      }
      setStatus(url ? '✅ Thumbnail override saved' : '✅ Thumbnail override cleared')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setThumbEditId(null)
    setThumbDraft('')
  }

  function openThumbEdit(video) {
    setThumbEditId(video.youtube_id || video.tbtv_id)
    setThumbDraft(video.thumbnail_override_url || '')
  }

  function cancelThumbEdit() {
    setThumbEditId(null)
    setThumbDraft('')
  }


  async function handlePreviewCSV() {
    try {
      const data = await api('/live-schedule/csv', { raw: true })
      const text = await data.text()
      setCsvPreview(text)
    } catch (e) {
      setStatus(`Error loading CSV: ${e.message}`)
    }
  }

  function handleDownloadCSV() {
    if (!csvPreview) return
    const blob = new Blob([csvPreview], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'live-schedule.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text)
    setStatus('✅ Copied')
  }

  const channelMap = useMemo(() => {
    const map = {}
    for (const ch of channels) map[ch.youtube_channel_id] = ch.name
    return map
  }, [channels])

  // Sorting
  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else { setSortKey(null); setSortDir('asc') }
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortedVideos = useMemo(() => {
    if (!sortKey) return videos
    const sorted = [...videos].sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return (a.title || '').localeCompare(b.title || '')
        case 'channel':
          return (channelMap[a.youtube_channel_id] || '').localeCompare(channelMap[b.youtube_channel_id] || '')
        case 'status':
          return (a.event_type || '').localeCompare(b.event_type || '')
        case 'date':
          return (new Date(a.scheduled_start || 0)).getTime() - (new Date(b.scheduled_start || 0)).getTime()
        case 'duration':
          return (a.duration_minutes || 0) - (b.duration_minutes || 0)
        case 'tbtv_channel':
          return (a.viloud_channel_name || '').localeCompare(b.viloud_channel_name || '')
        case 'tbtv_id':
          return (a.default_tbtv_channel_id || '').localeCompare(b.default_tbtv_channel_id || '')
        default:
          return 0
      }
    })
    return sortDir === 'desc' ? sorted.reverse() : sorted
  }, [videos, sortKey, sortDir, channelMap])

  const includedCount = videos.filter(v => v.include_in_schedule).length
  // Counts follow the shown status (v2 live status, falling back to event_type).
  const liveCount = videos.filter(v => liveStatusClass(v.live_v2_status, v.event_type) === 'live').length
  const upcomingCount = videos.filter(v => liveStatusClass(v.live_v2_status, v.event_type) === 'upcoming').length

  function SortHeader({ label, sortId, className }) {
    const active = sortKey === sortId
    return (
      <th className={`${className || ''} th-sortable`} onClick={() => handleSort(sortId)}>
        <span className="th-sort-inner">
          {label}
          {active ? (
            sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          ) : (
            <span className="th-sort-placeholder" />
          )}
        </span>
      </th>
    )
  }

  return (
    <div className="ls-container">
      {/* Header */}
      <div className="ls-header">
        <div className="ls-header-left">
          <h1><Calendar size={20} /> Live Schedule</h1>
          <div className="ls-stats">
            <span className="ls-stat">{includedCount} in schedule</span>
            {liveCount > 0 && <span className="ls-stat ls-stat-live"><Radio size={11} /> {liveCount} live</span>}
            <span className="ls-stat">{upcomingCount} upcoming</span>
          </div>
        </div>
        <div className="ls-header-right">
          <button className="btn-ghost" onClick={handlePreviewCSV} title="Preview CSV output">
            <Download size={14} /> CSV Preview
          </button>
          <button className="btn-ghost" onClick={loadVideos} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {status && (
        <div className={`ls-status ${status.startsWith('Error') ? 'error' : ''}`}>
          {status}
          <button className="btn-ghost btn-sm" onClick={() => setStatus('')}>✕</button>
        </div>
      )}

      {/* Info Banner */}
      <div className="ls-info-banner">
        <AlertCircle size={14} />
        <span>
          Events appear here from the <strong>YouTube Archive</strong> with status <em>upcoming</em> or <em>live</em>.
          Toggle <strong>✓ Include</strong> to add events to the master live schedule served to watch.tenband.tv.
        </span>
      </div>

      {/* Filters */}
      <div className="ls-filters">
        <label className="ls-toggle-filter">
          <input
            type="checkbox"
            checked={!showAll}
            onChange={e => setShowAll(!e.target.checked)}
          />
          Scheduled only
        </label>
        <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)}>
          <option value="">All Channels</option>
          {channels.map(ch => (
            <option key={ch.youtube_channel_id} value={ch.youtube_channel_id}>{ch.name}</option>
          ))}
        </select>
        <div className="ls-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Schedule Table */}
      <div className="ls-table-wrap">
        <table className="ls-table">
          <thead>
            <tr>
              <th className="th-include">Include</th>
              <th className="th-thumb"></th>
              <SortHeader label="Title" sortId="title" />
              <SortHeader label="Status" sortId="status" className="th-center" />
              <SortHeader label="Scheduled Start" sortId="date" />
              <SortHeader label="Duration" sortId="duration" className="th-center" />
              <SortHeader label="TBTV Channel" sortId="tbtv_channel" />
              <SortHeader label="Channel ID" sortId="tbtv_id" />
              <th className="th-cf">CF Input</th>
              <th className="th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="ls-loading">Loading...</td></tr>
            )}
            {!loading && sortedVideos.length === 0 && (
              <tr><td colSpan={10} className="ls-empty">
                {showAll
                  ? 'No upcoming or live events in YouTube Archive'
                  : 'No events in schedule. Toggle "Scheduled only" off to see all upcoming events.'}
              </td></tr>
            )}
            {!loading && sortedVideos.map(video => {
              const relative = formatRelative(video.scheduled_start)
              return (
                <tr
                  key={video.youtube_id || video.tbtv_id}
                  className={`ls-row ${video.include_in_schedule ? 'ls-row-included' : 'ls-row-excluded'}`}
                >
                  <td className="td-include">
                    <button
                      className={`ls-include-btn ${video.include_in_schedule ? 'active' : ''}`}
                      onClick={() => toggleSchedule(video)}
                      title={video.include_in_schedule ? 'Remove from schedule' : 'Add to schedule'}
                    >
                      {video.include_in_schedule ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                  </td>
                  <td className="td-thumb">
                    <div className="ls-thumb-cell">
                      <div className="ls-thumb-wrap">
                        <img
                          src={
                            video.thumbnail_override_url ||
                            video.thumbnail_url ||
                            `https://i.ytimg.com/vi/${video.youtube_id}/default.jpg`
                          }
                          alt=""
                          className="ls-thumb"
                        />
                        {video.thumbnail_override_url && (
                          <span className="ls-thumb-cf-badge" title="CF Images override active">CF</span>
                        )}
                        <button
                          className="ls-thumb-edit-btn"
                          onClick={() => openThumbEdit(video)}
                          title="Override thumbnail with Cloudflare Images URL"
                        >
                          <Image size={10} />
                        </button>
                      </div>
                      {thumbEditId === (video.youtube_id || video.tbtv_id) && (
                        <div className="ls-thumb-override-panel" onClick={e => e.stopPropagation()}>
                          <div className="ls-thumb-override-label">CF Images URL</div>
                          <input
                            className="ls-thumb-override-input"
                            type="url"
                            placeholder="https://imagedelivery.net/…"
                            value={thumbDraft}
                            onChange={e => setThumbDraft(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveThumbOverride(video)
                              if (e.key === 'Escape') cancelThumbEdit()
                            }}
                            autoFocus
                          />
                          <div className="ls-thumb-override-actions">
                            <button
                              className="btn-ghost btn-sm ls-thumb-save"
                              onClick={() => saveThumbOverride(video)}
                              title="Save override"
                            >
                              <Check size={11} /> Save
                            </button>
                            {video.thumbnail_override_url && (
                              <button
                                className="btn-ghost btn-sm ls-thumb-clear"
                                onClick={() => saveThumbOverride(video, null)}
                                title="Clear override, revert to YouTube"
                              >
                                <X size={11} /> Clear
                              </button>
                            )}
                            <button className="btn-ghost btn-sm" onClick={cancelThumbEdit}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="td-title">
                    <div className="ls-title-text">{video.title || 'Untitled'}</div>
                    <div className="ls-title-meta">
                      {channelMap[video.youtube_channel_id] || video.youtube_channel_id}
                    </div>
                  </td>
                  <td className="td-status">
                    <span className={`ls-status-badge ${liveStatusClass(video.live_v2_status, video.event_type)}`}>
                      {eventIcons[liveStatusClass(video.live_v2_status, video.event_type)]}
                      {liveStatusLabel(video.live_v2_status, video.event_type)}
                    </span>
                  </td>
                  <td className="td-date">
                    <div>{formatDate(video.scheduled_start)}</div>
                    {relative && relative !== 'past' && (
                      <div className="ls-relative">{relative}</div>
                    )}
                  </td>
                  <td className="td-duration">
                    <input
                      type="number"
                      className="ls-duration-input"
                      value={video.duration_minutes || ''}
                      placeholder="120"
                      onChange={e => {
                        const val = e.target.value ? parseInt(e.target.value) : null
                        setVideos(prev => prev.map(v =>
                          v.youtube_id === video.youtube_id ? { ...v, duration_minutes: val } : v
                        ))
                      }}
                      onBlur={e => {
                        const val = e.target.value ? parseInt(e.target.value) : null
                        updateField(video, 'duration_minutes', val)
                      }}
                    />
                    <span className="ls-duration-unit">min</span>
                  </td>
                  <td className="td-tbtv-channel">
                    <select
                      className="ls-channel-input"
                      value={tbtvChannels.find(tc => tc.viloud_id === video.viloud_channel_id)?.tbtv_channel_id || ''}
                      onChange={e => {
                        const ch = tbtvChannels.find(tc => tc.tbtv_channel_id === e.target.value)
                        if (ch) {
                          setVideos(prev => prev.map(v =>
                            v.youtube_id === video.youtube_id
                              ? { ...v, viloud_channel_name: ch.name, viloud_channel_id: ch.viloud_id }
                              : v
                          ))
                          updateField(video, 'viloud_channel_name', ch.name)
                          updateField(video, 'viloud_channel_id', ch.viloud_id)
                        }
                      }}
                    >
                      <option value="">—</option>
                      {tbtvChannels.map(tc => (
                        <option key={tc.viloud_id} value={tc.tbtv_channel_id}>
                          {tc.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="td-tbtv-id">
                    <span className="ls-tbtv-id" title={tbtvChannels.find(tc => tc.viloud_id === video.viloud_channel_id)?.tbtv_channel_id || '—'}>
                      {tbtvChannels.find(tc => tc.viloud_id === video.viloud_channel_id)?.tbtv_channel_id || '—'}
                    </span>
                  </td>
                  <td className="td-cf">
                    <select
                      className="ls-cf-select"
                      value={video.cf_live_input_id || ''}
                      onChange={e => {
                        setVideos(prev => prev.map(v =>
                          v.youtube_id === video.youtube_id
                            ? { ...v, cf_live_input_id: e.target.value }
                            : v
                        ))
                        updateField(video, 'cf_live_input_id', e.target.value)
                      }}
                    >
                      <option value="">—</option>
                      {cfLiveInputs.map(li => (
                        <option key={li.uid} value={li.uid}>
                          {li.meta?.name || li.uid.substring(0, 8)}
                        </option>
                      ))}
                    </select>
                    {sharedInputRiskIds.has(video.tbtv_id) && (
                      <span
                        title={`Another live event shares this CF input with a start within ${PRELIVE_MIN} min. The coordinator will flag "needs operator" and won't auto-start — you'll have to manually Go-Live one. Space the starts more than ${PRELIVE_MIN} min apart (or use different inputs) to avoid it.`}
                        style={{ marginLeft: 6, color: '#f59e0b', cursor: 'help', fontWeight: 700 }}
                      >⚠</span>
                    )}
                  </td>
                  <td className="td-actions">
                    {video.tbtv_id && video.cf_live_input_id &&
                      (!video.auto_start || video.live_v2_status === 'needs_operator') &&
                      !(!!video.actual_start || ['live', 'interrupted', 'drop_grace', 'ended'].includes(video.live_v2_status)) && (
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => goLiveNow(video)}
                        title={video.live_v2_status === 'needs_operator'
                          ? 'Needs operator: two events share this input — go live now to bind this one'
                          : 'Auto-Start is off — go live now (once the stream is hot)'}
                        style={{ color: '#22c55e', fontWeight: 700 }}
                      >
                        ▶
                      </button>
                    )}
                    {video.tbtv_id && video.cf_live_input_id &&
                      (!!video.actual_start || ['live', 'interrupted', 'drop_grace'].includes(video.live_v2_status)) && (
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => endEvent(video)}
                        title="End this live event (it has gone on air) — hands off to the next"
                        style={{ color: '#f87171', fontWeight: 700 }}
                      >
                        ■
                      </button>
                    )}
                    {video._source === 'content-library' && video.tbtv_id && (
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => openEditor(video)}
                        disabled={editLoading}
                        title="Edit this live event here (same editor as the content catalog)"
                        style={{ color: '#38bdf8' }}
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {video.tbtv_id && (
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => navigate(`/ondemand/catalog?select=${video.tbtv_id}`)}
                        title="Open in Content Library"
                        style={{ color: '#8b5cf6' }}
                      >
                        <Tv size={12} />
                      </button>
                    )}
                    <a
                      href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost btn-sm"
                      title="Open on YouTube"
                    >
                      <ExternalLink size={12} />
                    </a>
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => copyToClipboard(video.youtube_id)}
                      title="Copy Video ID"
                    >
                      <Copy size={12} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* CSV Preview Modal */}
      {csvPreview !== null && (
        <div className="ls-modal-overlay" onClick={() => setCsvPreview(null)}>
          <div className="ls-modal" onClick={e => e.stopPropagation()}>
            <div className="ls-modal-header">
              <h3>CSV Output Preview</h3>
              <div className="ls-modal-actions">
                <button className="btn-ghost" onClick={handleDownloadCSV}>
                  <Download size={14} /> Download
                </button>
                <button className="btn-ghost" onClick={() => copyToClipboard(csvPreview)}>
                  <Copy size={14} /> Copy
                </button>
                <button className="btn-ghost" onClick={() => setCsvPreview(null)}>✕</button>
              </div>
            </div>
            <div className="ls-csv-info">
              This is the CSV that watch.tenband.tv will receive. Column order and names
              match the Google Sheet format exactly.
            </div>
            <pre className="ls-csv-preview">{csvPreview}</pre>
          </div>
        </div>
      )}

      {/* Live Event Editor Modal — the SAME editor used in the content catalog, in-place here so
          Live Schedule is a full alternate view/editor of catalog live events, kept in sync. */}
      {editItem && (
        <div className="ls-modal-overlay" onClick={() => setEditItem(null)}>
          <div className="ls-editor-modal" onClick={e => e.stopPropagation()}>
            <div className="ls-modal-header">
              <h3><Radio size={16} /> Edit Live Event</h3>
              <div className="ls-modal-actions">
                <button className="btn-ghost" onClick={() => navigate(`/ondemand/catalog?select=${editItem.tbtv_id}`)} title="Open in the full Content Catalog">
                  <Tv size={14} /> Open in Catalog
                </button>
                <button className="btn-ghost" onClick={() => setEditItem(null)}>✕</button>
              </div>
            </div>
            <div className="ls-editor-body">
              <CatalogDetail
                item={editItem}
                allTags={allTags}
                onUpdate={handleEditorUpdate}
                onDelete={handleEditorDelete}
                onDuplicate={handleEditorDuplicate}
                setStatus={setStatus}
                layout="single"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
